import { Check, Circle, Eye, EyeOff } from 'lucide-react';
import * as React from 'react';
import { passwordStrength } from '@cfl/shared';

import { Input } from '@/components/cargofl-ui';
import { cn } from '@/lib/utils';

/**
 * Inputs for the dormant password sign-in (AUTH_PROVIDERS=password). The rules come from
 * @cfl/shared, so the meter turns red for exactly what the API refuses.
 */

/**
 * A password box with a show/hide eye. The toggle is a real button (keyboard reachable,
 * aria-pressed) that keeps focus in the field, so password managers and paste still work.
 */
export function PasswordInput({ className, ...props }: Omit<React.ComponentProps<typeof Input>, 'type'>) {
  const [shown, setShown] = React.useState(false);
  return (
    <div className="relative">
      <Input {...props} type={shown ? 'text' : 'password'} className={cn('pr-10', className)} />
      <button
        type="button"
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShown((s) => !s)}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

/** One token per level, too weak -> strong. */
const BAR = ['bg-destructive', 'bg-warning', 'bg-warning', 'bg-primary', 'bg-success'];
const TEXT = ['text-destructive', 'text-warning', 'text-warning', 'text-primary', 'text-success'];

/** Live meter under a "set a password" field. Renders nothing while the field is empty. */
export function PasswordStrength({ value, id }: { value: string; id?: string }) {
  if (!value) return null;
  const { score, label, checks, problem } = passwordStrength(value);
  return (
    <div id={id} className="space-y-2">
      <div
        role="progressbar"
        aria-label="Password strength"
        aria-valuemin={0}
        aria-valuemax={4}
        aria-valuenow={score}
        aria-valuetext={label}
        className="flex gap-1"
      >
        {[1, 2, 3, 4].map((step) => (
          <span
            key={step}
            className={cn('h-1.5 flex-1 rounded-full', step <= Math.max(score, 1) ? BAR[score] : 'bg-muted')}
          />
        ))}
      </div>
      <p className="text-xs text-muted-foreground" aria-live="polite">
        Password strength <span className={cn('ml-1 font-semibold', TEXT[score])}>{label}</span>
        {problem && <span className="text-destructive"> — {problem}</span>}
      </p>
      {/* Guidance only - none of these is required, and the API does not check them. */}
      <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        {checks.map((c) => (
          <li
            key={c.label}
            className={cn('flex items-center gap-1.5 text-xs', c.met ? 'text-success' : 'text-muted-foreground')}
          >
            {c.met ? <Check className="size-3.5" aria-hidden /> : <Circle className="size-3" aria-hidden />}
            <span className="sr-only">{c.met ? 'Done:' : 'Not yet:'}</span>
            {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Under a "confirm password" field: whether it matches yet. Nothing until something is typed. */
export function PasswordMatch({ password, confirm }: { password: string; confirm: string }) {
  if (!confirm) return null;
  const ok = password === confirm;
  return (
    <p aria-live="polite" className={cn('flex items-center gap-1.5 text-xs', ok ? 'text-success' : 'text-destructive')}>
      {ok && <Check className="size-3.5" aria-hidden />}
      {ok ? 'Passwords match' : "Passwords don't match yet"}
    </p>
  );
}
